import { useMemo, useState } from 'react';
import { useAccountContext } from '@gonfalon/app-context/useAccountContext';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { Alert, Link, ProgressBar } from '@launchpad-ui/components';

import { MS_PER_DAY } from '../../../constants';
import { AggregationType, DimensionType, Granularity, TimeRangeOption, UsageTab } from '../../../types';
import { usageTabConfig } from '../../constants/usageTabConfig';
import { useDataExportBreakdown } from '../../hooks/useDataExportBreakdown';
import { useDataExportSourceCapabilities } from '../../hooks/useDataExportSourceCapabilities';
import { useDataExportSourceUsage } from '../../hooks/useDataExportSourceUsage';
import { useUsageDetailSearchParams } from '../../hooks/useUsageDetailSearchParams';
import { useUsageMonthOptions } from '../../hooks/useUsageMonthOptions';
import { useUsageTimestamps } from '../../hooks/useUsageTimestamps';
import {
  DataExportScope,
  type DataExportSource,
  formatDataExportDataThrough,
  getDataExportChartView,
  getDataExportFreshness,
  getEffectiveDataExportScope,
  hasBothDataExportSources,
} from '../../utils/combineDataExportUsage';
import {
  getBreakdownDimensions,
  getBreakdownScope,
  getEffectiveBreakdownDimension,
} from '../../utils/dataExportBreakdown';
import { getEffectiveTo } from '../../utils/dates';
import { getGranularity } from '../../utils/getGranularity';
import { getSubscriptionLimits } from '../../utils/getSubscriptionLimits';
import { getUsageDisplayName } from '../../utils/getUsageDisplayName';
import { getMaxYValue, UsageDetailChart } from '../charts/UsageDetailChart';

import { UsageDetailControls } from './components';
import { DataExportBreakdownCards } from './components/DataExportBreakdownCards';
import { DataExportBreakdownCharts } from './components/DataExportBreakdownCharts';
import { DataExportCombinedSummaryCards } from './components/DataExportCombinedSummaryCards';
import { DataExportLegend, getDataExportScopeColor } from './components/DataExportLegend';

import styles from './UsageDetail.module.css';

const usageTab = UsageTab.DATA_EXPORT_COMBINED;

const DATA_EXPORT_USAGE_DOCS_URL = 'https://launchdarkly.com/docs/guides/integrations/data-export-usage';

type DataExportCombinedDetailProps = {
  defaultAggregationType: AggregationType;
  showTrialEnd?: boolean;
};

export function DataExportCombinedDetail({
  defaultAggregationType,
  showTrialEnd = false,
}: DataExportCombinedDetailProps) {
  const { subscription } = useAccountContext();
  const [scope, setScope] = useState(DataExportScope.BOTH);
  const { today, monthOptions, defaultMonth } = useUsageMonthOptions({
    count: usageTabConfig[usageTab].monthOptionCount,
  });

  const defaultParams = useMemo(
    () => ({ timeRange: TimeRangeOption.MONTH_TO_DATE, month: defaultMonth, aggregationType: defaultAggregationType }),
    [defaultMonth, defaultAggregationType],
  );
  const [params, setParams] = useUsageDetailSearchParams(usageTab, defaultParams, monthOptions);

  const granularity = getGranularity(params.timeRange);
  const timestamps = useUsageTimestamps({ timeRange: params.timeRange, month: params.month, today, granularity });
  const sources = useDataExportSourceUsage({
    from: timestamps.from,
    to: getEffectiveTo(timestamps.to, today, params.aggregationType),
    aggregationType: params.aggregationType,
    granularity,
  });

  const summaryEnd = timestamps.to - MS_PER_DAY;
  const monthToDateSources = useDataExportSourceUsage({
    from: Date.UTC.bind(Date)(new Date(summaryEnd).getUTCFullYear(), new Date(summaryEnd).getUTCMonth(), 1),
    to: timestamps.to,
    aggregationType: AggregationType.INCREMENTAL,
    granularity: Granularity.DAILY,
  });

  const capabilities = useDataExportSourceCapabilities();
  const hasBothSources = hasBothDataExportSources(capabilities, sources, monthToDateSources);
  const effectiveScope = getEffectiveDataExportScope(scope, capabilities, sources, monthToDateSources);
  const freshness = getDataExportFreshness(sources, effectiveScope);
  const breakdownScope = getBreakdownScope(effectiveScope, hasBothSources, capabilities, sources, monthToDateSources);
  const dimension = getEffectiveBreakdownDimension(params.dimension, breakdownScope);
  const breakdown = useDataExportBreakdown({
    dimension,
    scope: breakdownScope,
    timeRange: params.timeRange,
    from: timestamps.from,
    to: timestamps.to,
    today,
    granularity,
    aggregationType: params.aggregationType,
  });
  const view = getDataExportChartView({
    sources,
    scope: effectiveScope,
    aggregationType: params.aggregationType,
    granularity,
    timestamps,
    hasBothSources,
  });

  const { limitName, meterType, precision } = usageTabConfig[usageTab];
  const percentChangeTooltip =
    (params.timeRange ?? TimeRangeOption.MONTH_TO_DATE) === TimeRangeOption.MONTH_TO_DATE
      ? msg('billing.usageSummary.percentChangeTooltip')
      : msg('billing.dataExportCombined.previousPeriodTooltip');
  const limitValue = getSubscriptionLimits(subscription)?.[limitName] ?? 0;
  const trialEndUTC =
    showTrialEnd && subscription?.trialEndDate ? new Date(subscription.trialEndDate).getTime() : undefined;

  return (
    <div className={styles.container}>
      <div className={styles.titleGroup}>
        <h2>{getUsageDisplayName(usageTab, 'longTitleName')}</h2>
        <p className={styles.description}>
          <Message id="billing.dataExportCombined.description" />{' '}
          <Link href={DATA_EXPORT_USAGE_DOCS_URL} target="_blank">
            {msg('billing.dataExportCombined.reduceUsageLink')}
          </Link>
        </p>
      </div>
      <div className={styles.controlGroup}>
        <UsageDetailControls
          setParams={setParams}
          usageTab={usageTab}
          selectedTimeRange={params.timeRange}
          selectedMonth={params.month}
          monthOptions={monthOptions}
          selectedAggregationType={params.aggregationType}
          granularity={granularity}
          selectedDimension={dimension}
          availableDimensions={getBreakdownDimensions(breakdownScope)}
        >
          {breakdown ? (
            <DataExportBreakdownCards
              breakdown={breakdown}
              precision={precision}
              streamingOnly={
                hasBothSources && dimension === DimensionType.EVENT_KIND && breakdownScope === DataExportScope.BOTH
              }
              percentChangeTooltip={percentChangeTooltip}
            />
          ) : (
            <DataExportCombinedSummaryCards
              fromTimestampUTC={timestamps.from}
              toTimestampUTC={timestamps.to}
              timeRange={params.timeRange}
              today={today}
              precision={precision}
              percentChangeTooltip={percentChangeTooltip}
              showSourceCards={hasBothSources}
            />
          )}
        </UsageDetailControls>
      </div>

      <div className={styles.chartContainer}>
        <div className={styles.chartHeader}>
          <span className={styles.chartTitle}>
            <Message id="billing.dataExportCombined.totalUsage" />
          </span>
          {view.status === 'ready' && freshness && (
            <div className={styles.freshness}>
              {freshness.delayedSources.length > 0 && (
                <Alert status="warning" variant="inline" data-test-id="data-export-delayed">
                  {getDelayedMessage(freshness.delayedSources)}
                </Alert>
              )}
              <span className={styles.dataThrough} data-test-id="data-export-data-through">
                <Message
                  id="billing.dataExportCombined.latestRecordedUsage"
                  values={{ time: formatDataExportDataThrough(freshness.dataThrough, today) }}
                />
              </span>
            </div>
          )}
        </div>
        <div className={styles.chartContent}>
          {view.status === 'loading' && (
            <ProgressBar isIndeterminate aria-label={msg('billing.usagePage.loadingLabel')} />
          )}
          {view.status === 'error' && (
            <Alert status="error" data-test-id="data-export-combined-error">
              {getErrorMessage(effectiveScope, view.failedSources, hasBothSources)}
            </Alert>
          )}
          {view.status === 'ready' && (
            <UsageDetailChart
              data={view.data}
              size="large"
              condenseLabels={false}
              aggregationType={params.aggregationType}
              meterType={meterType}
              abbreviatedName={getUsageDisplayName(usageTab, 'abbreviated')}
              limitValue={view.showLimit ? limitValue : undefined}
              fromTimestampUTC={timestamps.from}
              toTimestampUTC={timestamps.to}
              cutoffTimestampUTC={timestamps.cutoff}
              yAxisMax={getMaxYValue(view.data.series, precision)}
              granularity={granularity}
              precision={precision}
              trialEndUTC={trialEndUTC}
              tooltipComponents={view.tooltipComponents}
              seriesColor={getDataExportScopeColor(effectiveScope)}
            />
          )}
        </div>
        {hasBothSources && <DataExportLegend selectedScope={scope} onChange={setScope} />}
      </div>

      {breakdown && dimension && (
        <DataExportBreakdownCharts
          key={dimension}
          breakdown={breakdown}
          dimension={dimension}
          aggregationType={params.aggregationType}
          timestamps={timestamps}
          abbreviatedName={getUsageDisplayName(usageTab, 'abbreviated')}
          meterType={meterType}
          granularity={granularity}
          precision={precision}
          trialEndUTC={trialEndUTC}
          seriesColor={getDataExportScopeColor(effectiveScope)}
        />
      )}
    </div>
  );
}

function getErrorMessage(scope: DataExportScope, failedSources: DataExportSource[], hasBothSources: boolean) {
  if (hasBothSources && scope === DataExportScope.BOTH && failedSources.length === 1) {
    const source =
      failedSources[0] === DataExportScope.STREAMING
        ? msg('billing.dataExportCombined.streaming')
        : msg('billing.dataExportCombined.warehouse');
    return msg('billing.dataExportCombined.combinedUnavailable', { source });
  }
  return msg('billing.usageCards.errorLoadingUsage');
}

function getDelayedMessage(delayedSources: DataExportSource[]) {
  if (delayedSources.length > 1) {
    return msg('billing.dataExportCombined.bothDelayed');
  }
  return delayedSources[0] === DataExportScope.STREAMING
    ? msg('billing.dataExportCombined.streamingDelayed')
    : msg('billing.dataExportCombined.warehouseDelayed');
}
